import { FC, useEffect } from 'react';
import useReactRouter from 'use-react-router';
import { Loading } from 'jbc-front/components/presenters/ui/Loading';

import { setTelemetryUser } from '@jbc-year-end-adj/common/telemetry';
import { useEmployeeQuery as useQuery } from '@jbc-year-end-adj/2024/hooks/graphql/useQuery';
import { useFetchQuestionGroups } from '@jbc-year-end-adj/2024/features/questions/hooks/employee/useFetchQuestionGroups';
import { Question as QuestionType } from '@jbc-year-end-adj/2024/features/questions/types';
import { ProgressBar } from '@jbc-year-end-adj/2024/features/questions/components/ProgressBar';
import { Sidebar } from '@jbc-year-end-adj/2024/features/questions/components/Sidebar';
import { dateFormat } from '@jbc-year-end-adj/2024/utils/formatter';

import { Question } from './parts/Question';
import { RequestComments } from './parts/RequestComments';
import { FETCH_EMPLOYEE_INPUT, QueryResult } from './query';
import styles from './EmployeeInput.scss';

// @ts-ignore
import { parse } from 'query-string';

// @ts-ignore
import { Redirect } from 'react-router';

export const EmployeeInput: FC = () => {
  const {
    location: { search },
    history
  } = useReactRouter();
  const { current }: { current?: string } = parse(search);

  const { loading, data } = useQuery<QueryResult>(FETCH_EMPLOYEE_INPUT, { fetchPolicy: 'network-only' });
  const { loading: questionGroupsLoading, data: questionGroupsData } = useFetchQuestionGroups();

  // datadogへログインユーザ情報を送信
  useEffect(() => {
    const employee = data?.request?.employee;
    if (employee) {
      setTelemetryUser({ id: `yea-employee${employee.id}` });
    }
  }, [data?.request?.employee]);

  if (loading || questionGroupsLoading) return <Loading />;
  if (!data?.request || !questionGroupsData) return <Redirect to="/employee_error?reason=session_expired" />;

  const {
    request: { employee, requestComments }
  } = data;

  const { clientSetting } = data;
  const submissionDeadlineAt = clientSetting?.submissionDeadline?.submissionDeadlineAt;

  if (!['in_progress', 'rejected'].includes(employee.status)) return <Redirect to="/employee_finished" />;
  if ('in_progress' === employee.status && new Date(submissionDeadlineAt) < new Date()) {
    return <Redirect to="/employee_expired" />;
  }

  const { questionGroups } = questionGroupsData;
  const questions = questionGroups.map(group => group.questions).flat();
  const notFinishedQuestions = questions.filter(question => question.status === 'not_answered');
  const answeringQuestion = questions.find(question => question.status === undefined || question.status === 'not_answered');
  const questionsByCode: { [key: QuestionType['code']]: QuestionType } = {};
  questions.forEach(question => (questionsByCode[question.code] = question));

  const firstNotAnsweredQuestion = notFinishedQuestions[0];
  const currentQuestion = current ? questionsByCode[current] : undefined;

  return (
    <div>
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">年末調整</h1>
        <p className="m-title-main-note">1画面ごとに自動保存されており入力途中から再開が可能です</p>
        {submissionDeadlineAt && (
          <p className="m-title-main-note">
            <span className={styles.notice}>提出期日: {dateFormat(submissionDeadlineAt, 'YYYY年M月D日 H時')}</span>
          </p>
        )}
      </div>

      <div className="l-contents-wrap">
        <div className="l-wrap-xxl">
          <div className={styles.progress}>
            <ProgressBar questionGroups={questionGroups} currentQuestion={currentQuestion} />
          </div>
          {requestComments?.length > 0 && <RequestComments employee={employee} />}
          <div className={styles.wrap}>
            <div className={styles.main}>
              <Question
                currentQuestion={currentQuestion}
                firstNotAnsweredQuestion={firstNotAnsweredQuestion}
                questionsByCode={questionsByCode}
              />
            </div>

            <Sidebar
              questionGroups={questionGroups}
              currentQuestionCode={currentQuestion?.code}
              answeringQuestionCode={answeringQuestion?.code}
              onChangeCode={code => {
                history.push(`/employee_input?current=${code}`);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
